"use client";
// ===================================================
// מחברת — small controls shared by the workspace parts.
// Built on @/components/ui (BRAND §Components — one home); colours are tokens.
// ===================================================
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui";

/** Segmented choice. Keyboard: native button focus order; state via aria-pressed. */
export function Seg<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly { v: T; label: string; emoji?: string }[];
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex items-center gap-0.5 rounded-lg border border-[var(--theme-border)] bg-[var(--theme-surface)] p-0.5"
    >
      {options.map((o) => {
        const on = value === o.v;
        return (
          <Button
            key={o.v}
            type="button"
            size="sm"
            variant="ghost"
            aria-pressed={on}
            onClick={() => onChange(o.v)}
            className={
              on
                ? "bg-[var(--role-selection-bg)] text-[var(--role-selection-fg)]"
                : "text-[var(--theme-text-muted)]"
            }
            style={{ height: 24, paddingInline: 8, borderRadius: 6 }}
          >
            {o.emoji ? <span aria-hidden>{o.emoji}</span> : null}
            {o.label}
          </Button>
        );
      })}
    </div>
  );
}

/** Icon-only action with a visible tooltip and an accessible name. */
export function IconAction({
  icon,
  label,
  onClick,
  active,
  disabled,
  className = "",
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      icon={icon}
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`${active ? "text-[var(--theme-primary)]" : "text-[var(--theme-text-muted)]"} ${className}`}
      style={{ width: 28, height: 28, padding: 0, borderRadius: 6 }}
    />
  );
}
